import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ButtonLink, Card, cx } from '../../components/ui';
import { api, formatHours } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useLiveUsage } from '../../lib/liveUsage';
import { usePageTitle } from '../../lib/usePageTitle';
import { formatDate } from './Credentials';

export function Overview() {
  usePageTitle('Overview — WaveQA');

  const { user } = useAuth();
  // Subscription and runs both move when an agent reports a run, with no
  // mutation in this tab to invalidate them; `useLiveUsage` is what keeps the
  // hours on this page honest. Credentials change on setup, which the
  // credentials page already polls for.
  const { subscription, runs } = useLiveUsage();
  const credentials = useQuery({ queryKey: ['credentials'], queryFn: api.getCredentials });

  const activeCredentials =
    credentials.data?.filter((c) => c.status === 'active')
      .length ?? 0;
  const recentRuns = runs.data?.slice(0, 5) ?? [];

  // Nothing set up yet — show the path forward rather than four empty widgets.
  const gettingStarted = activeCredentials === 0;

  return (
    <div>
      <h2 className="text-lg font-semibold text-white">
        Welcome{user?.display_name ? `, ${user.display_name}` : ''}
      </h2>

      {gettingStarted && !credentials.isPending && (
        <Card className="mt-5 border-surge-600/40 bg-surge-500/5">
          <h3 className="text-base font-semibold text-white">Get your first run going</h3>
          <ol className="mt-4 space-y-3 text-sm text-hull-300">
            <Step n={1}>
              <Link to="/app/credentials" className="text-surge-400 hover:text-surge-300">
                Create an agent credential
              </Link>{' '}
              — the secret is shown once, so copy it when it appears.
            </Step>
            <Step n={2}>
              Install the CLI and run <Code>waveqa setup</Code> on the machine that
              will run verifications.
            </Step>
            <Step n={3}>
              Run <Code>waveqa -p &quot;YourProject&quot;</Code>, fill in the four
              documents it creates, then run it again.
            </Step>
          </ol>
          <div className="mt-5">
            <ButtonLink to="/app/credentials">Create a credential</ButtonLink>
          </div>
        </Card>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat
          label="Plan"
          value={subscription.data?.plan ?? '—'}
          detail={subscription.data?.status.replace('_', ' ')}
          loading={subscription.isPending}
          className="capitalize"
        />
        <Stat
          label="Hours remaining"
          value={
            subscription.data ? formatHours(subscription.data.hours_remaining) : '—'
          }
          detail={
            subscription.data
              ? `of ${formatHours(subscription.data.monthly_hour_allowance)} this period`
              : undefined
          }
          loading={subscription.isPending}
        />
        <Stat
          label="Agents"
          value={
            subscription.data
              ? `${activeCredentials} / ${subscription.data.agent_limit}`
              : String(activeCredentials)
          }
          detail="credentials in use"
          loading={credentials.isPending}
        />
        <Stat
          label="QA runs"
          // The account's real total, not the length of the activity list —
          // that list is a capped page, so counting it froze this tile at the
          // cap and made reporting look broken.
          value={
            subscription.data ? String(subscription.data.qa_runs_total) : '—'
          }
          detail="reported"
          loading={subscription.isPending}
        />
      </div>

      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-white">Recent runs</h3>
          {recentRuns.length > 0 && (
            <Link to="/app/usage" className="text-sm text-surge-400 hover:text-surge-300">
              View all
            </Link>
          )}
        </div>

        {recentRuns.length === 0 ? (
          <p className="rounded-card border border-dashed border-hull-700 p-6 text-center text-sm text-hull-400">
            No runs reported yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {recentRuns.map((run) => (
              <li
                key={run.run_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-hull-700 bg-hull-800/40 px-4 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm text-white">{run.project_name}</p>
                  <p className="text-xs text-hull-400">{formatDate(run.started_at)}</p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-pass-400">{run.criteria_passed} passed</span>
                  {run.criteria_failed > 0 && (
                    <span className="text-fail-400">{run.criteria_failed} failed</span>
                  )}
                  <span
                    className={cx(
                      'rounded-full border px-2 py-0.5',
                      run.status === 'completed'
                        ? 'border-pass-600/40 text-pass-400'
                        : run.status === 'failed'
                          ? 'border-fail-600/40 text-fail-400'
                          : 'border-hull-600 text-hull-400',
                    )}
                  >
                    {run.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  detail,
  loading,
  className,
}: {
  label: string;
  value: string;
  detail?: string;
  loading?: boolean;
  className?: string;
}) {
  return (
    <Card>
      <p className="text-xs uppercase tracking-wider text-hull-400">{label}</p>
      {loading ? (
        <div className="mt-2 h-7 w-20 animate-pulse rounded bg-hull-700" />
      ) : (
        <p className={cx('mt-1 text-2xl font-semibold text-white', className)}>{value}</p>
      )}
      {detail && !loading && <p className="mt-1 text-xs text-hull-400">{detail}</p>}
    </Card>
  );
}

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-hull-700 text-xs font-semibold text-surge-300">
        {n}
      </span>
      <span>{children}</span>
    </li>
  );
}

function Code({ children }: { children: React.ReactNode }) {
  return (
    <code className="rounded border border-hull-700 bg-hull-950 px-1.5 py-0.5 font-mono text-xs text-surge-300">
      {children}
    </code>
  );
}
